<template>
  <UISection
    class="translation-section w-full overflow-visible"
    :scrollable="false"
  >
    <template #title>
      {{ $t('pages.localization.add_translation') }}
    </template>

    <template #title-append>
      <LangDropdown
        v-if="langProfileList?.length"
        class="lang-dropdown text-white"
        v-model="selectedLangProfile"
        lang-code-key="iso_code"
        id-key="language_profile_id"
        :options="langProfileList"
        :disabled="isLoading"
      />
    </template>

    <Loader v-if="isLoading" />
    <div
      v-else-if="selectedLangProfileTranslations"
      class="flex flex-col w-full gap-4"
    >
      <UITitle>
        {{ selectedLangProfile?.name }}
      </UITitle>
      <TranslationList
        :sections="sectionList"
        :saveMethod="handleSave"
        @change="handleChange"
      />
    </div>
    <Empty
      v-else
      classList="text-lg"
      :showBtn="false"
    />
  </UISection>
</template>


<script>
import { mapGetters, mapActions } from "vuex";
import Loader from "@shared/components/WrappedLoader";
import Empty from "@shared/empty";
import { TranslationList } from './components';
import handleError from "@shared/mixins/handleError";
import LangDropdown from "@shared/components/LangDropdown";
import { checkPermission } from "@shared/utils/functions";
import TranslationKeysHelper from "@shared/modules/localization/utils/translationKeysHelper";
import { capitalize, cloneDeep } from "lodash";

export default {
  name: "translation-tab",
  mixins: [handleError],
  components: {
    Loader,
    Empty,
    TranslationList,
    LangDropdown,
  },
  props: {},
  provide() {
    return {
      langProfile: () => this.selectedLangProfile,
    }
  },
  data() {
    return {
      isLoading: false,
      translationsHelper: null,
    };
  },
  computed: {
    ...mapGetters({
      getLangProfileList: "LocalizationStore/getLangProfileList",
      getTranslations: "LocalizationStore/getTranslations", // TODO check
    }),

    langProfileList() {
      return this.getLangProfileList ?? [];
    },

    selectedLangProfile: {
      get() {
        if (!this.selectedLangProfileId) return null;
        return this.langProfileList?.find((el) => el.language_profile_id === this.selectedLangProfileId) || null;
      },
      async set(val) {
        this.selectedLangProfileId = val?.language_profile_id || null;

        if (val) {
          try {
            this.isLoading = true;
            await this.initLangProfileTranslations();
            this.isLoading = false;
          } catch (error) {
            console.error("Failed to set language profile", error);
            this.handleError(error);
          }
        }
      }
    },

    selectedLangProfileTranslations() {
      if (!this.selectedLangProfileId) return null;
      return this.getTranslations[this.selectedLangProfileId] || null;
    },

    selectedLangProfileId: {
      get() {
        return this.$route.query.language_profile_id || null;
      },
      set(val) {
        // eslint-disable-next-line no-unused-vars
        const { language_profile_id, ...newQueryParams } = this.$route.query;
        if (val) {
          newQueryParams.language_profile_id = val;

          this.$router.push({
            path: this.$route.path,
            query: newQueryParams,
          });
        }
      },
    },

    sectionList() {
      return Object.entries(this.translationsHelper?.organized || {})
        .map(([sec_key, sec_value]) => ({
          id: sec_key,
          name: sec_key,
          label: this.getSectionLabel(sec_key),
          keys: (Object.entries(sec_value) || [])
            .map(([el_key, el_value]) => {
              const keyName = this.getKeyLabel(sec_key, el_key);
              return {
                id: el_key,
                name: el_key,
                label: keyName,
                value: el_value,
              }
            })
        }))
    },
  },

  async mounted() {
    this.isLoading = true;
    try {
      if (!this.langProfileList?.length) {
        await this.fetchLangProfileList();
      }
      if (!this.selectedLangProfileId) {
        this.selectedLangProfileId = this.langProfileList?.[0]?.language_profile_id || null;
      }
      if (this.selectedLangProfile) {
        await this.initLangProfileTranslations();
      }
    } catch (error) {
      console.error("Failed to init translations tab", error);
      this.handleError(error);
    }
    this.isLoading = false;
  },

  methods: {
    ...mapActions({
      fetchLangProfileList: "LocalizationStore/fetchLangProfileList",
      fetchLangProfileTranslations: "LocalizationStore/fetchLangProfileTranslations",
      updateLangProfileTranslations: "LocalizationStore/updateLangProfileTranslations",
    }),

    async initLangProfileTranslations() {
      await this.fetchLangProfileTranslations(this.selectedLangProfile);
      if (!this.selectedLangProfileTranslations) return;
      const translations = cloneDeep(this.selectedLangProfileTranslations);
      this.translationsHelper = new TranslationKeysHelper(translations);
    },

    handleChange({ key, value, section }) {
      if (!key || !section) return;
      this.translationsHelper.setDeepKeyValue(`${section}.${key}`, value);
    },

    async handleSave() {
      if (!checkPermission('localization.edit') || !this.translationsHelper?.translations) return;
      try {
        await this.updateLangProfileTranslations({
          id: this.selectedLangProfileId,
          payload: {
            translations: cloneDeep(this.translationsHelper.translations),
          }
        });
        this.$toast.success(this.$t("general.update_success"));
      } catch (error) {
        console.error("Failed to save translations", error);
        this.handleError(error);
        throw error;
      }
    },

    getSectionLabel(key) {
      return this.$t(`pages.localization.${key}`)
        || capitalize( // fallback if we don't have appropriate translation
          String(key)
            .split('_')
            .join(' ')
            .toLocaleLowerCase()
          ) || key;
    },
    getKeyLabel(sectionKey, key) {
      return capitalize(
          this.$t(`${sectionKey}.${key}`)
            ?.toLocaleLowerCase()
        || key
      );
    },
  },

};
</script>

<style lang="scss" scoped>
.translation-section.card-base {
  height: 100%;
}

.translation-section.card-base .card-base__content {
  height: 100%;
}

::v-deep {
  .lang-dropdown {
    .multiselect {
      @apply text-white;
      &__tags {
        .multiselect__single .custom-label svg {
          @apply text-white;
        }
      }
      .multiselect__single .custom-label__text {
        @apply text-white;
      }
    }
  }
}
</style>
